import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  orderBy,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from "firebase/firestore";

import { db } from "../../FirebaseConfig";
import { SavingOverlay } from "../../components/SavingOverlay";
import { useAuth } from "../../lib/auth-context";
import { useCollection } from "../../lib/firestore-hooks";
import { formatRut } from "../../lib/format";
import type { CuentaDoc, PasajeroDoc, UsuarioDoc } from "../../lib/types";

interface UsuarioRow extends UsuarioDoc {
  id: string;
}

type FilterKey =
  | "all"
  | "login"
  | "nologin"
  | "admin"
  | "approver"
  | "user"
  | "pending"
  | "pilot"
  | "deleted";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "Todos" },
  { key: "login", label: "Con login" },
  { key: "nologin", label: "Sin login" },
  { key: "admin", label: "Admins" },
  { key: "approver", label: "Aprueban" },
  { key: "user", label: "Usuarios" },
  { key: "pending", label: "Pendientes" },
  { key: "pilot", label: "Pilotos" },
  { key: "deleted", label: "Eliminados" },
];

/**
 * /admin/users — v1-style single list with filter pills at the top.
 *
 * Identity model on Firestore docs:
 *   - authUid + not legacy_  →  "login" user (signed in at least once)
 *   - roles.includes("user") + no login  →  "pending" (pre-approved)
 *   - isAdmin / isSuperAdmin  →  admin (with optional "approver" tag)
 *   - roles.includes("pilot")  →  pilot
 *   - isDeleted  →  hidden from normal filters; surfaces in "Eliminados"
 *
 * Login gating is enforced in `hasAccess()` (auth-context). The `user`
 * tag is the canonical login switch.
 */
export function AdminUsuarios() {
  const location = useLocation();
  // Caller may pass `{ state: { from } }` when navigating in so we
  // show a context-aware back link instead of forcing the admin to
  // use the nav. Mirrors the same pattern AdminFlightDetail uses.
  const fromState = (location.state as { from?: string } | null)?.from;
  const { rows } = useCollection<UsuarioDoc>("Usuarios", orderBy("displayName"));
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [search, setSearch] = useState("");

  const { filtered, counts } = useMemo(() => {
    const r = rows ?? [];
    function match(u: UsuarioRow, k: FilterKey): boolean {
      const authUid = (u as unknown as { authUid?: string | null }).authUid;
      const isLogin = !!authUid && !u.id.startsWith("legacy_");
      const hasUserTag = u.roles?.includes("user") ?? false;
      const hasPilotTag = u.roles?.includes("pilot") ?? false;
      const hasApproverTag = u.roles?.includes("approver") ?? false;
      const isAdmin = u.isAdmin || u.isSuperAdmin;
      switch (k) {
        case "all":
          return !u.isDeleted;
        case "login":
          return !u.isDeleted && isLogin;
        case "nologin":
          return !u.isDeleted && !isLogin;
        case "admin":
          return !u.isDeleted && isAdmin;
        case "approver":
          return !u.isDeleted && isAdmin && hasApproverTag;
        case "user":
          return !u.isDeleted && (hasUserTag || isAdmin);
        case "pending":
          return !u.isDeleted && !isLogin && hasUserTag;
        case "pilot":
          return !u.isDeleted && hasPilotTag;
        case "deleted":
          return u.isDeleted;
      }
    }
    const term = search.trim().toLowerCase();
    const filtered = r.filter((u) => {
      if (!match(u, filter)) return false;
      if (!term) return true;
      const hay = `${u.displayName ?? ""} ${u.email ?? ""} ${(u.roles ?? []).join(" ")}`.toLowerCase();
      return hay.includes(term);
    });
    const counts: Record<FilterKey, number> = {
      all: r.filter((u) => match(u, "all")).length,
      login: r.filter((u) => match(u, "login")).length,
      nologin: r.filter((u) => match(u, "nologin")).length,
      admin: r.filter((u) => match(u, "admin")).length,
      approver: r.filter((u) => match(u, "approver")).length,
      user: r.filter((u) => match(u, "user")).length,
      pending: r.filter((u) => match(u, "pending")).length,
      pilot: r.filter((u) => match(u, "pilot")).length,
      deleted: r.filter((u) => match(u, "deleted")).length,
    };
    return { filtered, counts };
  }, [rows, filter, search]);

  if (!rows) return <p className="text-sm text-slate-500">Cargando…</p>;

  return (
    <div className="space-y-5">
      {fromState ? (
        <Link
          to={fromState}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          {fromState === "/admin/notifications"
            ? "← Volver a notificaciones"
            : fromState.startsWith("/admin/flights/")
              ? "← Volver al vuelo"
              : "← Volver"}
        </Link>
      ) : null}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-2 h-[3px] w-8 rounded-full bg-brand-700" />
          <h1 className="text-2xl font-semibold tracking-tight text-brand-900">Usuarios</h1>
          <p className="mt-1 text-sm text-slate-600">
            {filtered.length} en este filtro. La columna{" "}
            <strong>Usuario</strong> habilita el ingreso a la app.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900"
        >
          + Nuevo contacto / piloto
        </button>
      </header>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar nombre, email o rol…"
        className="block w-full max-w-md rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
      />

      <nav className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const active = f.key === filter;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={
                "rounded-full px-3 py-1 text-xs font-medium transition " +
                (active
                  ? "bg-brand-700 text-white"
                  : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-brand-50")
              }
            >
              {f.label}
              <span
                className={
                  "ml-1.5 " +
                  (active ? "text-slate-300" : "text-slate-500")
                }
              >
                {counts[f.key]}
              </span>
            </button>
          );
        })}
      </nav>

      <UserTable rows={filtered} editable kind="login" />

      {creating ? (
        <DirectoryEntryEditor onClose={() => setCreating(false)} />
      ) : null}
    </div>
  );
}

function UserTable({
  rows,
  editable,
  kind,
}: {
  rows: UsuarioRow[];
  editable: boolean;
  kind: "login" | "directory";
}) {
  const [editing, setEditing] = useState<UsuarioRow | null>(null);

  // Pilot-only people (the directory entries that just receive
  // assignment emails and don't log in) sink to the bottom with a
  // thicker divider, so the top of the list is the people who
  // actually use the app.
  const { topRows, pilotOnlyRows } = useMemo(() => {
    const top: UsuarioRow[] = [];
    const bot: UsuarioRow[] = [];
    for (const u of rows) {
      const isAdmin = u.isAdmin || u.isSuperAdmin;
      const hasUser = u.roles?.includes("user") ?? false;
      const hasApprover = u.roles?.includes("approver") ?? false;
      const hasPilot = u.roles?.includes("pilot") ?? false;
      const isPilotOnly = hasPilot && !isAdmin && !hasUser && !hasApprover;
      (isPilotOnly ? bot : top).push(u);
    }
    return { topRows: top, pilotOnlyRows: bot };
  }, [rows]);

  // Roles + columns are now condensed into a single "Roles" cell so
  // the table doesn't try to be an inline editor. Editing happens in
  // the modal with an explicit Save.
  const colCount = 6;

  function renderCard(u: UsuarioRow, dividerAbove: boolean) {
    return (
      <li
        key={u.id}
        onClick={() => setEditing(u)}
        className={
          "cursor-pointer rounded-lg border border-slate-200 bg-white shadow-sm p-3 hover:bg-sky-50 "
          + (dividerAbove ? "mt-3 border-t-4 border-t-slate-300" : "")
        }
      >
        <div className="flex items-baseline justify-between gap-2">
          <div>
            <div className="font-medium text-slate-900">
              {u.displayName || "(sin nombre)"}
            </div>
            <div className="flex flex-wrap items-baseline gap-1.5 font-mono text-xs text-slate-600">
              {u.email || "—"}
              {u.bounceCount && u.bounceCount > 0 ? (
                <span
                  className="rounded bg-rose-100 px-1 text-[10px] font-medium uppercase text-rose-800"
                  title="Emails que rebotaron desde la última vez. Verificar dirección."
                >
                  bounce ×{u.bounceCount}
                </span>
              ) : null}
            </div>
            {u.lastSignInAt ? (
              <div className="text-[11px] text-slate-500">
                Último login · {formatLastSignIn(u.lastSignInAt)}
              </div>
            ) : null}
            <div className="text-[11px] text-slate-500">
              Pax link ·{" "}
              {u.passengerId ? (
                <span className="font-semibold text-emerald-600">✓</span>
              ) : (
                <span className="font-semibold text-rose-500">✗</span>
              )}
            </div>
          </div>
        </div>
        <RoleBadges u={u} />
      </li>
    );
  }

  function renderRow(u: UsuarioRow, dividerAbove: boolean) {
    return (
      <tr
        key={u.id}
        onClick={() => setEditing(u)}
        className={
          "cursor-pointer hover:bg-sky-50 "
          + (dividerAbove ? "border-t-4 border-t-slate-300" : "")
        }
      >
        <td className="whitespace-nowrap px-2 py-1.5 text-slate-900">
          {u.displayName}
        </td>
        <td className="whitespace-nowrap px-2 py-1.5 text-center font-mono text-xs text-slate-700">
          {u.initials || "—"}
        </td>
        <td className="whitespace-nowrap px-2 py-1.5 font-mono text-xs text-slate-700">
          {u.email || "—"}
          {u.bounceCount && u.bounceCount > 0 ? (
            <span
              className="ml-1 rounded bg-rose-100 px-1 py-0.5 text-[10px] font-medium uppercase text-rose-800"
              title="Emails que rebotaron. Verificar dirección."
            >
              bounce ×{u.bounceCount}
            </span>
          ) : null}
        </td>
        <td className="whitespace-nowrap px-2 py-1.5 font-mono text-xs text-slate-500">
          {formatLastSignIn(u.lastSignInAt ?? null)}
        </td>
        <td
          className="whitespace-nowrap px-2 py-1.5 text-center"
          title={u.passengerId ? "Vinculado a una ficha de pasajero" : "Sin pasajero vinculado"}
        >
          {u.passengerId ? (
            <span className="font-semibold text-emerald-600">✓</span>
          ) : (
            <span className="font-semibold text-rose-500">✗</span>
          )}
        </td>
        <td className="w-full px-2 py-1.5">
          <RoleBadges u={u} compact />
        </td>
      </tr>
    );
  }

  void editable;
  void kind;

  return (
    <>
      {/* Mobile cards. Desktop table renders on md+. */}
      <ul className="space-y-2 md:hidden">
        {rows.length === 0 ? (
          <li className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-center text-sm text-slate-500">
            Sin entradas.
          </li>
        ) : (
          <>
            {topRows.map((u) => renderCard(u, false))}
            {pilotOnlyRows.map((u, i) =>
              renderCard(u, i === 0 && topRows.length > 0)
            )}
          </>
        )}
      </ul>

      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm md:block">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="whitespace-nowrap px-2 py-2">Nombre</th>
              <th className="whitespace-nowrap px-2 py-2 text-center"></th>
              <th className="whitespace-nowrap px-2 py-2">Email</th>
              <th className="whitespace-nowrap px-2 py-2">Último login</th>
              <th className="whitespace-nowrap px-2 py-2 text-center">Pax Link</th>
              <th className="w-full px-2 py-2">Roles</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="px-3 py-6 text-center text-sm text-slate-500"
                >
                  Sin entradas.
                </td>
              </tr>
            ) : (
              <>
                {topRows.map((u) => renderRow(u, false))}
                {pilotOnlyRows.map((u, i) =>
                  renderRow(u, i === 0 && topRows.length > 0)
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
      {editing ? (
        <UsuarioEditor initial={editing} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
}

function RoleBadges({ u, compact }: { u: UsuarioRow; compact?: boolean }) {
  const badges: { label: string; cls: string }[] = [];
  if (u.isSuperAdmin) {
    badges.push({ label: "super", cls: "bg-violet-100 text-violet-800" });
  } else if (u.isAdmin) {
    badges.push({ label: "admin", cls: "bg-violet-100 text-violet-800" });
  }
  if (u.roles?.includes("approver")) {
    badges.push({ label: "aprueba", cls: "bg-sky-100 text-sky-800" });
  }
  if (!u.isAdmin && !u.isSuperAdmin && u.roles?.includes("user")) {
    badges.push({ label: "usuario", cls: "bg-emerald-100 text-emerald-800" });
  }
  const hasPilot = u.roles?.includes("pilot") ?? false;
  if (hasPilot) {
    badges.push({ label: "piloto", cls: "bg-amber-100 text-amber-800" });
  }
  // Pre-login indicator: directory entries with no Firebase Auth UID
  // linked yet. The createUserDoc trigger will auto-merge their
  // permissions onto the UID-keyed doc on first sign-in.
  const authUid = (u as unknown as { authUid?: string | null }).authUid;
  const isLogin = !!authUid && !u.id.startsWith("legacy_");
  if (!u.isDeleted && !isLogin) {
    badges.push({ label: "pre-login", cls: "bg-slate-100 text-slate-600" });
  }
  if (u.isDeleted) {
    badges.push({ label: "eliminado", cls: "bg-rose-100 text-rose-800" });
  }
  if (badges.length === 0) {
    return (
      <span className={(compact ? "" : "mt-2 inline-block ") + "text-[11px] text-slate-400"}>
        sin permisos
      </span>
    );
  }

  const hasId = !!u.rut;
  const hasLicense = !!(u.licenseDgac || u.licenseFaa);
  const idTitle = hasId ? `RUT: ${formatRut(u.rut!)}` : "Sin RUT ingresado";
  const licTitle = hasLicense
    ? [u.licenseDgac ? `DGAC: ${u.licenseDgac}` : null, u.licenseFaa ? `FAA: ${u.licenseFaa}` : null]
        .filter(Boolean)
        .join(" · ")
    : "Sin licencia ingresada";

  return (
    <div className={(compact ? "" : "mt-2 ") + "flex flex-wrap gap-1"}>
      {badges.map((b) => (
        <span
          key={b.label}
          className={
            "rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide " +
            b.cls
          }
        >
          {b.label}
        </span>
      ))}
      {hasPilot && (
        <span className="ml-2 flex items-center gap-2.5 border-l border-slate-200 pl-2.5">
          <span
            title={idTitle}
            className={"text-[11px] font-medium " + (hasId ? "text-emerald-600" : "text-rose-600")}
          >
            RUT {hasId ? "✓" : "✗"}
          </span>
          <span
            title={licTitle}
            className={"text-[11px] font-medium " + (hasLicense ? "text-emerald-600" : "text-rose-600")}
          >
            Lic {hasLicense ? "✓" : "✗"}
          </span>
        </span>
      )}
    </div>
  );
}

function UsuarioEditor({
  initial,
  onClose,
}: {
  initial: UsuarioRow;
  onClose: () => void;
}) {
  const { usuario: currentUser } = useAuth();
  // Self-edit guard: admins can't lock themselves out by accident.
  // Disables the admin / deleted / user-role toggles when the editor
  // is open on the signed-in admin's own doc.
  const isSelf = !!currentUser && currentUser.id === initial.id;
  const isLogin =
    !!(initial as unknown as { authUid?: string | null }).authUid &&
    !initial.id.startsWith("legacy_");
  const { rows: accounts } = useCollection<CuentaDoc>("Cuentas", orderBy("code"));
  const accountOptions = (accounts ?? []).filter((a) => a.active);
  const { rows: pasajeros } = useCollection<PasajeroDoc>("Pasajeros", orderBy("fullName"));
  const pasajeroOptions = (pasajeros ?? []).filter((p) => p.active);
  const [passengerId, setPassengerId] = useState(initial.passengerId ?? "");
  const [displayName, setDisplayName] = useState(initial.displayName ?? "");
  const [email, setEmail] = useState(initial.email ?? "");
  const [initials, setInitials] = useState(initial.initials ?? "");
  const [defaultAccountCode, setDefaultAccountCode] = useState(
    initial.defaultAccountCode ?? ""
  );
  const [accountCodes, setAccountCodes] = useState<string[]>(
    initial.accountCodes ?? []
  );
  const [licenseDgac, setLicenseDgac] = useState(initial.licenseDgac ?? "");
  const [licenseFaa, setLicenseFaa] = useState(initial.licenseFaa ?? "");
  const [manifestLicense, setManifestLicense] = useState<
    "dgac" | "faa" | "both"
  >(initial.manifestLicense ?? "dgac");
  const [rut, setRut] = useState(initial.rut ?? "");
  // Role flags become staged local state — only persisted on Save. No
  // more "click the wrong checkbox in the list and it's already live".
  const [isAdmin, setIsAdmin] = useState(initial.isAdmin);
  const [hasApprover, setHasApprover] = useState(
    initial.roles?.includes("approver") ?? false
  );
  const [hasUser, setHasUser] = useState(
    initial.roles?.includes("user") ?? false
  );
  const [hasPilot, setHasPilot] = useState(
    initial.roles?.includes("pilot") ?? false
  );
  const [isDeleted, setIsDeleted] = useState(initial.isDeleted);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleAccount(code: string) {
    setAccountCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  }

  async function save() {
    setError(null);
    const name = displayName.trim();
    const mail = email.trim();
    if (!name) return setError("Nombre obligatorio.");
    // defaultAccountCode must be one of the allowed codes (if any).
    const def = defaultAccountCode.trim().toUpperCase() || null;
    if (def && accountCodes.length > 0 && !accountCodes.includes(def)) {
      return setError("La cuenta por defecto debe estar entre las permitidas.");
    }
    const nextRoles = [
      ...(hasUser ? ["user"] : []),
      ...(hasPilot ? ["pilot"] : []),
      ...(hasApprover ? ["approver"] : []),
    ];

    // Block self-locking. Super-admin is already protected at the
    // checkbox level; this catches the regular-admin → not-admin case
    // and the self-delete case before they reach Firestore.
    if (isSelf && initial.isAdmin && !isAdmin) {
      return setError(
        "No puedes quitarte el permiso de admin a ti mismo. Pide a otro admin que lo haga."
      );
    }
    if (isSelf && isDeleted) {
      return setError(
        "No puedes marcarte como eliminado a ti mismo. Pide a otro admin que lo haga."
      );
    }

    // Confirm destructive role changes — anything that removes access
    // or downgrades the user. Non-destructive toggles (adding roles,
    // pilot on/off, approver on/off) save silently.
    const initialHasUser = initial.roles?.includes("user") ?? false;
    const destructive: string[] = [];
    if (initial.isAdmin && !isAdmin) destructive.push("Quitar admin");
    if (initialHasUser && !hasUser) destructive.push("Quitar acceso de usuario");
    if (!initial.isDeleted && isDeleted) destructive.push("Marcar como eliminado");
    if (destructive.length > 0) {
      const ok = window.confirm(
        `Confirmar cambios para ${initial.displayName ?? initial.email ?? "este usuario"}:\n\n` +
          destructive.map((s) => "  • " + s).join("\n") +
          "\n\n¿Continuar?"
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      // Explicit initials win; if cleared we fall back to derivation.
      const finalInitials = initials.trim().toUpperCase() || initialsOf(name, mail);
      await updateDoc(doc(db, "Usuarios", initial.id), {
        displayName: name,
        email: mail || null,
        initials: finalInitials,
        defaultAccountCode: def,
        accountCodes: accountCodes.length > 0 ? accountCodes : null,
        isAdmin: initial.isSuperAdmin ? true : isAdmin,
        isDeleted,
        roles: nextRoles,
        // Pilot credentials. Stored on every user (typed `?` in
        // UsuarioDoc) but only consumed when this user is the
        // assigned pilot on a flight; harmless empty strings.
        licenseDgac: licenseDgac.trim() || null,
        licenseFaa: licenseFaa.trim() || null,
        manifestLicense,
        rut: rut.trim() || null,
        // Links this user to their passenger record so Claude can resolve
        // "el pasajero soy yo".
        passengerId: passengerId || null,
        updatedAt: serverTimestamp(),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <SavingOverlay open={saving} label="Guardando usuario…" />
      <div
        className="my-8 w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-900">
          Editar usuario
        </h2>
        {isLogin ? (
          <p className="mt-1 text-xs text-slate-500">
            Esta cuenta está vinculada a Google. Cambiar el email acá NO
            cambia el email con que inicia sesión — solo el correo que
            usamos para notificaciones.
          </p>
        ) : null}
        <div className="mt-4 space-y-4 text-sm">
          <div>
            <label className="block font-medium text-slate-700">
              Nombre completo
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <div>
              <label className="block font-medium text-slate-700">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@dominio.cl"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
            <div>
              <label className="block font-medium text-slate-700">
                Iniciales
              </label>
              <input
                type="text"
                value={initials}
                onChange={(e) =>
                  setInitials(e.target.value.toUpperCase().slice(0, 4))
                }
                placeholder={initialsOf(displayName, email)}
                maxLength={4}
                className="mt-1 block w-20 rounded-md border border-slate-300 px-3 py-2 text-center font-mono uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
          </div>
          <div>
            <label className="block font-medium text-slate-700">
              Cuentas permitidas
            </label>
            <p className="mt-1 text-xs text-slate-500">
              Cuentas a las que este usuario puede cargar vuelos. Si no
              marcas ninguna, podrá usar cualquier cuenta activa.
            </p>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {accountOptions.map((a) => (
                <label
                  key={a.code}
                  className="inline-flex items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={accountCodes.includes(a.code)}
                    onChange={() => toggleAccount(a.code)}
                    className="rounded border-slate-300"
                  />
                  <span>
                    <span className="font-mono">{a.code}</span>{" "}
                    <span className="text-slate-500">— {a.displayName}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block font-medium text-slate-700">
              Cuenta por defecto (opcional)
            </label>
            <select
              value={defaultAccountCode}
              onChange={(e) => setDefaultAccountCode(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            >
              <option value="">— ninguna —</option>
              {(accountCodes.length > 0
                ? accountOptions.filter((a) => accountCodes.includes(a.code))
                : accountOptions
              ).map((a) => (
                <option key={a.code} value={a.code}>
                  {a.code} — {a.displayName}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              Se autocompleta al solicitar un vuelo.
            </p>
          </div>
          <div>
            <label className="block font-medium text-slate-700">
              Pasajero vinculado (opcional)
            </label>
            <select
              value={passengerId}
              onChange={(e) => setPassengerId(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            >
              <option value="">— ninguno —</option>
              {pasajeroOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName}
                  {p.rut ? ` — ${formatRut(p.rut)}` : ""}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              La ficha de pasajero que ES esta persona. Permite que Claude
              resuelva "el pasajero soy yo" al pedir un vuelo.
            </p>
          </div>
          {hasPilot ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                Datos de piloto
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Se usan para autocompletar el Manifiesto de pasajeros.
              </p>
              <div className="mt-2 grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700">
                    Licencia DGAC
                  </label>
                  <input
                    type="text"
                    value={licenseDgac}
                    onChange={(e) => setLicenseDgac(e.target.value)}
                    maxLength={32}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-1.5 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700">
                    Licencia FAA
                  </label>
                  <input
                    type="text"
                    value={licenseFaa}
                    onChange={(e) => setLicenseFaa(e.target.value)}
                    maxLength={32}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-1.5 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700">
                    RUT
                  </label>
                  <input
                    type="text"
                    value={rut}
                    onChange={(e) => setRut(e.target.value)}
                    maxLength={16}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-1.5 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
                  />
                </div>
              </div>
              {licenseDgac.trim() && licenseFaa.trim() ? (
                <div className="mt-3">
                  <p className="text-xs font-medium text-slate-700">
                    Licencia a mostrar en el manifiesto
                  </p>
                  <div className="mt-1.5 flex gap-4">
                    {(
                      [
                        ["dgac", "DGAC"],
                        ["faa", "FAA"],
                        ["both", "Ambas"],
                      ] as const
                    ).map(([val, label]) => (
                      <label
                        key={val}
                        className="inline-flex items-center gap-1.5 text-sm text-slate-800"
                      >
                        <input
                          type="radio"
                          name="manifestLicense"
                          checked={manifestLicense === val}
                          onChange={() => setManifestLicense(val)}
                          className="border-slate-300"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          <div>
            <label className="block font-medium text-slate-700">Roles</label>
            <p className="mt-1 text-xs text-slate-500">
              Los cambios se guardan al confirmar abajo.
            </p>
            <div className="mt-2 space-y-1.5">
              <label className="flex items-start gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  checked={initial.isSuperAdmin ? true : isAdmin}
                  disabled={initial.isSuperAdmin || (isSelf && initial.isAdmin)}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300"
                />
                <span>
                  Admin
                  <span className="block text-xs text-slate-500">
                    {isSelf && initial.isAdmin
                      ? "No puedes quitarte el admin a ti mismo."
                      : "Acceso total a /admin/*. Super-admins quedan siempre con admin activo."}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  checked={hasApprover}
                  disabled={!(initial.isSuperAdmin ? true : isAdmin)}
                  onChange={(e) => setHasApprover(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300"
                />
                <span>
                  Aprueba (solo admins)
                  <span className="block text-xs text-slate-500">
                    Recibe emails de solicitudes pendientes y notificaciones
                    de cambios de vuelo.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  checked={hasUser}
                  onChange={(e) => setHasUser(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300"
                />
                <span>
                  Usuario
                  <span className="block text-xs text-slate-500">
                    Permite iniciar sesión en la app. Admins ya tienen acceso
                    implícito.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  checked={hasPilot}
                  onChange={(e) => setHasPilot(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300"
                />
                <span>
                  Piloto
                  <span className="block text-xs text-slate-500">
                    Aparece en el selector de piloto al solicitar vuelos.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  checked={isDeleted}
                  disabled={initial.isSuperAdmin || isSelf}
                  onChange={(e) => setIsDeleted(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300"
                />
                <span>
                  Eliminado
                  <span className="block text-xs text-slate-500">
                    {isSelf
                      ? "No puedes marcarte como eliminado a ti mismo."
                      : "Bloquea el acceso a la app sin borrar el registro."}
                  </span>
                </span>
              </label>
            </div>
          </div>
          {error ? (
            <p className="rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
              {error}
            </p>
          ) : null}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={save}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DirectoryEntryEditor({ onClose }: { onClose: () => void }) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [isPilot, setIsPilot] = useState(true);
  const [isUser, setIsUser] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    if (!displayName.trim()) return setError("Nombre obligatorio.");
    const roles: string[] = [];
    if (isPilot) roles.push("pilot");
    if (isUser) roles.push("user");
    setSaving(true);
    try {
      await addDoc(collection(db, "Usuarios"), {
        displayName: displayName.trim(),
        email: email.trim() || null,
        initials: initialsOf(displayName, email),
        isAdmin: false,
        isSuperAdmin: false,
        isDeleted: false,
        roles,
        defaultAccountCode: null,
        authUid: null,
        createdAt: serverTimestamp(),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <SavingOverlay open={saving} label="Guardando contacto…" />
      <div
        className="my-8 w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-900">
          Nuevo contacto / piloto
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Crea una entrada de directorio sin login. Aparecerá en los
          selectores de piloto y recibirá emails de asignación.
        </p>
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Nombre completo
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Email (opcional)
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="piloto@ejemplo.cl"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
            <p className="mt-1 text-xs text-slate-500">
              Si lo proporcionas, recibirá emails de asignación.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={isPilot}
              onChange={(e) => setIsPilot(e.target.checked)}
              className="rounded border-slate-300"
            />
            Es piloto (aparece en el selector de piloto en /solicitar)
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={isUser}
              onChange={(e) => setIsUser(e.target.checked)}
              className="mt-0.5 rounded border-slate-300"
            />
            <span>
              Es usuario de la app
              <span className="block text-xs text-slate-500">
                Habilitará el ingreso cuando esta persona inicie sesión
                con Google. Para pilotos que solo reciben emails, dejar
                desmarcado.
              </span>
            </span>
          </label>
          {error ? (
            <p className="rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
              {error}
            </p>
          ) : null}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={save}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Returns a short, locale-aware "last login" cell. */
function formatLastSignIn(ts: Timestamp | null): string {
  if (!ts) return "—";
  const d = ts.toDate();
  return new Intl.DateTimeFormat("es-CL", {
    timeZone: "America/Santiago",
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

function initialsOf(name: string, email: string): string {
  const src = name || email || "";
  if (!src) return "";
  const parts = src.replace(/@.*$/, "").split(/[\s.\-_]+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
